import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import "../../styles/App.css";
import { getRoomDetails } from "../../api/dss.js";
import { GENDER_LABELS, PERSON_GENDER_LABELS } from "../../decision/allocationLabels.js";

// Картка кімнати (/allocation/rooms/:id): дані кімнати, стать блоку, поєднання
// курсів, інші кімнати блоку і мешканці. Значення, яких немає в даних,
// показуються червоним «не вказано». Якщо DSS недоступний (503, degraded),
// ядро все одно віддає кімнату й мешканців -- показуємо їх з попередженням.

const MISSING_TEXT = "не вказано";

const Missing = () => <span className="missing-value">{MISSING_TEXT}</span>;

const isBlank = (value) => value === null || value === undefined || String(value).trim() === "";

const valueOrMissing = (value) => (isBlank(value) ? <Missing /> : value);

const personGender = (gender) =>
  isBlank(gender) ? <Missing /> : PERSON_GENDER_LABELS[gender] || <Missing />;

// Порушення мешканця: число або масив подій.
function violationsCount(violations) {
  if (Array.isArray(violations)) return violations.length;
  return typeof violations === "number" ? violations : null;
}

// Скільки мешканців без статі/курсу: missingData від сервера (число), а якщо
// його немає -- рахуємо самі за списком мешканців.
function missingCount(missingData, key, residents, isMissing) {
  const fromServer = missingData?.[key];
  if (typeof fromServer === "number") return fromServer;
  return residents.filter(isMissing).length;
}

function BlockGender({ gender }) {
  if (isBlank(gender) || gender === "unknown") {
    return (
      <span className="missing-value" title="У блоці є мешканці без вказаної статі">
        {GENDER_LABELS.unknown}
      </span>
    );
  }
  return <>{GENDER_LABELS[gender] || gender}</>;
}

function AccessibleValue({ accessible }) {
  if (accessible === true) return <>так</>;
  if (accessible === false) return <>ні</>;
  return <Missing />;
}

const RoomDetailsPage = () => {
  const { id } = useParams();
  const [details, setDetails] = useState(null);
  const [degraded, setDegraded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError("");
      setDegraded(false);
      setDetails(null);
      try {
        const result = await getRoomDetails(id);
        if (!cancelled) setDetails(result);
      } catch (err) {
        console.error(err);
        if (cancelled) return;
        if (err.status === 503 && err.data?.room) {
          setDetails(err.data);
          setDegraded(true);
        } else if (err.status === 404) {
          setError("Кімнату не знайдено.");
        } else if (err.status === 503) {
          setError("Модуль підтримки рішень зараз недоступний, спробуйте пізніше.");
        } else {
          setError("Не вдалося отримати дані кімнати.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const room = details?.room;
  const residents = details?.residents || [];
  const block = details?.block;
  const otherRooms = (block?.rooms || []).filter((r) => String(r.id) !== String(room?.id));
  const noGender = missingCount(details?.missingData, "gender", residents, (r) => isBlank(r.gender));
  const noCourse = missingCount(details?.missingData, "course", residents, (r) => isBlank(r.course));

  return (
    <div className="page-container">
      <Link to="/allocation/rooms" className="back-link">
        <span aria-hidden="true">←</span> До вільних кімнат
      </Link>

      {loading && <p className="text-header">Завантаження...</p>}
      {error && (
        <p className="text-header" role="alert" style={{ color: "var(--full-busy)" }}>
          {error}
        </p>
      )}

      {room && (
        <>
          <h3 className="text-header">Кімната {valueOrMissing(room.number)}</h3>

          {degraded && (
            <p className="text-header" role="alert" data-testid="room-degraded" style={{ color: "var(--full-busy)" }}>
              Модуль підтримки рішень зараз недоступний: показано лише дані кімнати та мешканців; стать блоку,
              поєднання курсів та інші кімнати блоку можуть бути неповними.
            </p>
          )}

          <div className="input-component" data-testid="room-info">
            <div className="form-container">
              <p className="input-block text-header">
                <strong>Гуртожиток:</strong> {valueOrMissing(room.dormitoryName)}
              </p>
              <p className="input-block text-header">
                <strong>Блок:</strong> {valueOrMissing(room.block)}
              </p>
              <p className="input-block text-header">
                <strong>Місткість:</strong> {valueOrMissing(room.capacity)}
              </p>
              <p className="input-block text-header">
                <strong>Вільних місць:</strong> {valueOrMissing(room.freeBeds)}
              </p>
              <p className="input-block text-header">
                <strong>Доступна для осіб з інвалідністю:</strong>{" "}
                <AccessibleValue accessible={room.accessible} />
              </p>
              {block && (
                <p className="input-block text-header">
                  <strong>Стать блоку:</strong> <BlockGender gender={block.gender} />
                </p>
              )}
            </div>

            {details.courseMix && (
              <p className="text-header" data-testid="room-course-mix" style={{ color: "var(--full-busy)" }}>
                ⚠ У кімнаті разом мешкають студенти 1 курсу і 4+ курсу (порушено м'яке обмеження).
              </p>
            )}
            {(noGender > 0 || noCourse > 0) && (
              <p className="text-header missing-value" data-testid="room-missing-data">
                Неповні дані мешканців: без статі — {noGender}, без курсу — {noCourse}. Такі мешканці не
                враховуються, коли визначається стать блоку чи поєднання курсів.
              </p>
            )}
          </div>

          {block && (
            <div className="input-component" data-testid="block-rooms">
              <h3 className="text-header">Інші кімнати блоку {valueOrMissing(room.block)}</h3>
              {otherRooms.length > 0 ? (
                <ul>
                  {otherRooms.map((r) => (
                    <li key={r.id} className="text-header">
                      <Link to={`/allocation/rooms/${r.id}`}>{r.number}</Link>: вільно {r.freeBeds} з {r.capacity}
                      {r.courseMix && (
                        <span title="Разом курс 1 і курс 4+" style={{ color: "var(--full-busy)" }}>
                          {" "}
                          ⚠ курси 1 і 4+
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="no-data">У блоці немає інших кімнат.</p>
              )}
            </div>
          )}

          <h3 className="text-header">Мешканці ({residents.length})</h3>
          {residents.length > 0 ? (
            <table className="table" data-testid="room-residents">
              <thead>
                <tr>
                  <th className="table-header">ПІБ</th>
                  <th className="table-header">Група</th>
                  <th className="table-header">Стать</th>
                  <th className="table-header">Курс</th>
                  <th className="table-header">Порушення</th>
                </tr>
              </thead>
              <tbody>
                {residents.map((r) => {
                  const violations = violationsCount(r.violations);
                  return (
                    <tr key={r.id}>
                      <td className="table-content">
                        <Link to={`/student/${r.id}`}>{isBlank(r.pib) ? <Missing /> : r.pib}</Link>
                      </td>
                      <td className="table-content">{valueOrMissing(r.group)}</td>
                      <td className="table-content">{personGender(r.gender)}</td>
                      <td className="table-content">{valueOrMissing(r.course)}</td>
                      <td className="table-content">{violations === null ? "—" : violations}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="no-data">У кімнаті немає мешканців.</p>
          )}
        </>
      )}
    </div>
  );
};

export default RoomDetailsPage;
